import React, { useState, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { Resizable } from 'react-resizable';

// --- Manual-resize constraints for the chart plot area ---
// Each axis fills its container responsively until the user drags the matching
// handle (south = height, east = width, south-east corner = both), after which
// that dimension becomes a manual value clamped to the limits below.
const MIN_CHART_HEIGHT = 200;       // fallback minimum height — never collapse below this
const MAX_CHART_HEIGHT = 2000;      // absolute ceiling height — never grow beyond this
const FALLBACK_CHART_HEIGHT = 400;  // height used before the container can be measured
const MIN_CHART_WIDTH = 280;        // fallback minimum width — never collapse below this
const MAX_CHART_WIDTH = 4000;       // absolute ceiling width — never grow beyond this
const FALLBACK_CHART_WIDTH = 600;   // width used before the container can be measured

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

// --- Chart colour palette (Rebellionaire: deep black + gold) ---
const COLORS = {
  ma28:      '#f0c040',
  ma14:      '#c9a84c',
  ma7:       '#e0892a',
  daily:     'rgba(201,168,76,0.3)',
  stock:     '#d0d0d0',
  starAvg:   'rgba(78,205,196,0.4)',
  starMa7:   '#7eddd6',
  starMa14:  '#4ecdc4',
  starMa28:  '#2ab5ac',
};

const VIEWS = [
  '28-Day MA', '14-Day MA', '7-Day MA', 'Daily', 'Stock Price',
  'Star 28-Day MA', 'Star 14-Day MA', 'Star 7-Day MA', 'Avg Rating',
];

const DEFAULT_VIEWS = new Set([
  '28-Day MA', '14-Day MA', '7-Day MA', 'Daily', 'Stock Price',
  'Star 28-Day MA',
]);

// Preset range options: label → days back from today (null = show all)
const PRESETS = [
  { label: '7D',  days: 7 },
  { label: '14D', days: 14 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: 'All', days: null },
];

/** Subtract N days from today and return an ISO string. */
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

/**
 * Format a date string 'YYYY-MM-DD' to a short label like 'Jan 24'.
 */
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Custom tooltip shown on chart hover.
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const formatValue = (name, value) => {
    if (typeof value !== 'number') return value;
    if (name === 'Stock Price') return `$${value.toFixed(2)}`;
    if (name.startsWith('Star') || name === 'Avg Rating') return `${value.toFixed(2)} ★`;
    return value.toFixed(1);
  };

  return (
    <div
      style={{
        backgroundColor: '#111008',
        border: '1px solid #3a2e00',
        borderRadius: 6,
        padding: '10px 14px',
        fontSize: '0.8rem',
        color: '#fff',
      }}
    >
      <div style={{ marginBottom: 6, color: '#9a8a60' }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
          {p.name}:{' '}
          <strong>{formatValue(p.name, p.value)}</strong>
        </div>
      ))}
    </div>
  );
};

/**
 * CompanyReviewsChart
 *
 * Renders the URI-style line + bar chart.  Toggle buttons at the top-right
 * mirror the original design.
 *
 * Props:
 *   records  {Array}  — DailyRecord objects from the API
 *   title    {string} — Chart section title (default: 'URI — Upstart Review Index')
 */
// Stable style reference — prevents recharts v3 from dispatching on every render
const LEGEND_WRAPPER_STYLE = { fontSize: '0.75rem', color: '#9a8a60', paddingTop: 8 };

const CompanyReviewsChart = ({ records = [], title = 'Review Activity', style = {} }) => {
  const [activeViews, setActiveViews] = useState(DEFAULT_VIEWS);

  // --- Time range state ---
  // activePreset: one of PRESETS[].label, or 'Custom'
  const [activePreset, setActivePreset] = useState('All');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo]     = useState('');
  const [showCustom, setShowCustom] = useState(false);

  // --- Manual resize state ---
  // Each dimension auto-fills its container (responsive) until the user drags
  // the matching handle, after which it becomes a manual, clamped value. The
  // two axes lock independently: resizing height leaves width responsive, and
  // vice versa; the corner handle locks both.
  const chartAreaRef = useRef(null);
  const userResizedWidthRef = useRef(false);
  const userResizedHeightRef = useRef(false);
  const [chartWidth, setChartWidth] = useState(FALLBACK_CHART_WIDTH);
  const [chartHeight, setChartHeight] = useState(FALLBACK_CHART_HEIGHT);

  // Follow the container for any axis the user hasn't manually resized yet.
  useLayoutEffect(() => {
    const el = chartAreaRef.current;
    if (!el) return undefined;
    const sync = () => {
      if (!userResizedWidthRef.current && el.clientWidth > 0) {
        setChartWidth(clamp(el.clientWidth, MIN_CHART_WIDTH, MAX_CHART_WIDTH));
      }
      if (!userResizedHeightRef.current && el.clientHeight > 0) {
        setChartHeight(clamp(el.clientHeight, MIN_CHART_HEIGHT, MAX_CHART_HEIGHT));
      }
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleResize = useCallback((_e, { size, handle }) => {
    // `handle` is the axis string ('s', 'e', 'se', …); lock only the dragged axes.
    if (handle.includes('e')) {
      userResizedWidthRef.current = true;
      setChartWidth(clamp(size.width, MIN_CHART_WIDTH, MAX_CHART_WIDTH));
    }
    if (handle.includes('s')) {
      userResizedHeightRef.current = true;
      setChartHeight(clamp(size.height, MIN_CHART_HEIGHT, MAX_CHART_HEIGHT));
    }
  }, []);

  // Custom drag handles styled to match the gold theme. Positioning and cursor
  // depend on which edge/corner the handle represents.
  const renderResizeHandle = useCallback((handleAxis, ref) => {
    const base = {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      touchAction: 'none',
      zIndex: 2,
    };
    const grip = { borderRadius: 2, backgroundColor: '#3a2e00' };

    if (handleAxis === 'e') {
      return (
        <div
          ref={ref}
          className="react-resizable-handle react-resizable-handle-e"
          title="Drag to resize"
          style={{ ...base, top: '50%', right: 0, transform: 'translateY(-50%)', width: 12, height: 64, cursor: 'ew-resize' }}
        >
          <div style={{ ...grip, width: 3, height: 40 }} />
        </div>
      );
    }
    if (handleAxis === 'se') {
      return (
        <div
          ref={ref}
          className="react-resizable-handle react-resizable-handle-se"
          title="Drag to resize"
          style={{ ...base, bottom: 0, right: 0, width: 16, height: 16, cursor: 'nwse-resize' }}
        >
          <div style={{ width: 8, height: 8, borderRight: '2px solid #3a2e00', borderBottom: '2px solid #3a2e00' }} />
        </div>
      );
    }
    // South (height) handle — default.
    return (
      <div
        ref={ref}
        className="react-resizable-handle react-resizable-handle-s"
        title="Drag to resize"
        style={{ ...base, bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 64, height: 12, cursor: 'ns-resize' }}
      >
        <div style={{ ...grip, width: 40, height: 3 }} />
      </div>
    );
  }, []);

  const toggleView = (view) => {
    setActiveViews((prev) => {
      const next = new Set(prev);
      if (next.has(view)) {
        if (next.size > 1) next.delete(view);
      } else {
        next.add(view);
      }
      return next;
    });
  };

  const applyPreset = (preset) => {
    setActivePreset(preset.label);
    setShowCustom(false);
    setCustomFrom('');
    setCustomTo('');
  };

  const applyCustom = () => {
    setActivePreset('Custom');
    setShowCustom(false);
  };

  // Filter records based on the active range
  const visibleRecords = useMemo(() => {
    if (activePreset === 'Custom') {
      return records.filter((r) => {
        if (customFrom && r.date < customFrom) return false;
        if (customTo   && r.date > customTo)   return false;
        return true;
      });
    }
    const preset = PRESETS.find((p) => p.label === activePreset);
    if (!preset || preset.days === null) return records;
    const cutoff = daysAgoISO(preset.days);
    return records.filter((r) => r.date >= cutoff);
  }, [records, activePreset, customFrom, customTo]);

  // Determine the min and max dates in the full dataset for clamping the pickers
  const minDate = records.length ? records[0].date : '';
  const maxDate = records.length ? records[records.length - 1].date : '';

  // Down-sample x-axis labels for readability
  const tickInterval = useMemo(() => {
    if (visibleRecords.length <= 30) return 0;
    return Math.floor(visibleRecords.length / 30);
  }, [visibleRecords.length]);

  if (!records.length) {
    return (
      <div
        className="rounded p-4 text-center"
        style={{ backgroundColor: '#111008', border: '1px solid #3a2e00', color: '#9a8a60' }}
      >
        No chart data available.
      </div>
    );
  }

  const starAxisVisible = ['Star 28-Day MA','Star 14-Day MA','Star 7-Day MA','Avg Rating']
    .some((v) => activeViews.has(v));

  const datePickerStyle = {
    backgroundColor: '#1a1400',
    border: '1px solid #3a2e00',
    borderRadius: 6,
    color: '#c9a84c',
    fontSize: '0.75rem',
    padding: '3px 8px',
    outline: 'none',
    colorScheme: 'dark',
  };

  return (
    <div
      className="rounded p-3"
      style={{ backgroundColor: '#111008', border: '1px solid #3a2e00', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, ...style }}
    >
      {/* Header row */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap" style={{ gap: 8 }}>
        <div style={{ fontSize: '1rem', fontWeight: 600, color: '#fff' }}>
          <span
            style={{
              display: 'inline-block',
              width: 4,
              height: '1em',
              backgroundColor: '#c9a84c',
              marginRight: 8,
              verticalAlign: 'middle',
              borderRadius: 2,
            }}
          />
          {title}
        </div>

        {/* Right side controls */}
        <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>

          {/* Preset range buttons */}
          <div className="d-flex" style={{ gap: 4 }}>
            {PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => applyPreset(p)}
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  padding: '3px 10px',
                  borderRadius: 20,
                  border: '1px solid #3a2e00',
                  cursor: 'pointer',
                  backgroundColor: activePreset === p.label ? '#c9a84c' : '#1a1400',
                  color: activePreset === p.label ? '#000' : '#8a7a50',
                  transition: 'all 0.15s',
                }}
              >
                {p.label}
              </button>
            ))}
            {/* Custom range button */}
            <button
              onClick={() => setShowCustom((v) => !v)}
              style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                padding: '3px 10px',
                borderRadius: 20,
                border: '1px solid #3a2e00',
                cursor: 'pointer',
                backgroundColor: activePreset === 'Custom' ? '#c9a84c' : '#1a1400',
                color: activePreset === 'Custom' ? '#000' : '#8a7a50',
                transition: 'all 0.15s',
              }}
            >
              Custom
            </button>
          </div>

          {/* Toggle buttons */}
          <div className="d-flex" style={{ gap: 6, flexWrap: 'wrap' }}>
            {VIEWS.map((v) => (
              <button
                key={v}
                onClick={() => toggleView(v)}
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  padding: '4px 12px',
                  borderRadius: 20,
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: activeViews.has(v) ? '#c9a84c' : '#1e1800',
                  color: activeViews.has(v) ? '#000' : '#8a7a50',
                  transition: 'all 0.15s',
                }}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Custom date range picker (shown inline below header when toggled) */}
      {showCustom && (
        <div
          className="d-flex align-items-center mb-2"
          style={{ gap: 8, flexWrap: 'wrap' }}
        >
          <span style={{ fontSize: '0.75rem', color: '#9a8a60' }}>From</span>
          <input
            type="date"
            value={customFrom}
            min={minDate}
            max={customTo || maxDate}
            onChange={(e) => setCustomFrom(e.target.value)}
            style={datePickerStyle}
          />
          <span style={{ fontSize: '0.75rem', color: '#9a8a60' }}>To</span>
          <input
            type="date"
            value={customTo}
            min={customFrom || minDate}
            max={maxDate}
            onChange={(e) => setCustomTo(e.target.value)}
            style={datePickerStyle}
          />
          <button
            onClick={applyCustom}
            style={{
              fontSize: '0.72rem',
              fontWeight: 600,
              padding: '4px 14px',
              borderRadius: 20,
              border: 'none',
              cursor: 'pointer',
              backgroundColor: '#c9a84c',
              color: '#000',
            }}
          >
            Apply
          </button>
        </div>
      )}

      {/* Chart — fills the container by default, manually resizable in both directions */}
      <div ref={chartAreaRef} style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Resizable
          axis="both"
          width={chartWidth}
          height={chartHeight}
          minConstraints={[MIN_CHART_WIDTH, MIN_CHART_HEIGHT]}
          maxConstraints={[MAX_CHART_WIDTH, MAX_CHART_HEIGHT]}
          resizeHandles={['s', 'e', 'se']}
          handle={renderResizeHandle}
          onResize={handleResize}
        >
          <div style={{ position: 'relative', width: chartWidth, height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={visibleRecords} margin={{ top: 4, right: 56, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2a2000" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            interval={tickInterval}
            tick={{ fill: '#7a6a40', fontSize: 11 }}
            axisLine={{ stroke: '#2a2000' }}
            tickLine={false}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: '#7a6a40', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={36}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tickFormatter={(v) => `$${v.toFixed(0)}`}
            tick={{ fill: '#a0a0a0', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          {/* Star rating axis: fixed 1–5 domain */}
          <YAxis
            yAxisId="star"
            orientation="right"
            domain={[1, 5]}
            ticks={[1, 2, 3, 4, 5]}
            tickFormatter={(v) => `${v}★`}
            tick={{ fill: '#4ecdc4', fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            width={36}
            hide={!starAxisVisible}
          />
          <Tooltip content={CustomTooltip} />
          <Legend
            wrapperStyle={LEGEND_WRAPPER_STYLE}
            iconType="line"
          />

          {/* Daily reviews bar */}
          {activeViews.has('Daily') && (
            <Bar
              yAxisId="left"
              dataKey="reviews_count"
              name="Daily Reviews"
              fill={COLORS.daily}
              radius={[2, 2, 0, 0]}
              maxBarSize={8}
            />
          )}

          {/* Moving average lines */}
          {activeViews.has('28-Day MA') && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="ma_28"
              name="28-Day MA"
              stroke={COLORS.ma28}
              dot={false}
              strokeWidth={2}
              isAnimationActive={false}
            />
          )}
          {activeViews.has('14-Day MA') && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="ma_14"
              name="14-Day MA"
              stroke={COLORS.ma14}
              dot={false}
              strokeWidth={2}
              isAnimationActive={false}
            />
          )}
          {activeViews.has('7-Day MA') && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="ma_7"
              name="7-Day MA"
              stroke={COLORS.ma7}
              dot={false}
              strokeWidth={2}
              isAnimationActive={false}
            />
          )}

          {/* Stock price line (right axis) */}
          {activeViews.has('Stock Price') && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="stock_price"
              name="Stock Price"
              stroke={COLORS.stock}
              dot={false}
              strokeWidth={2}
              strokeDasharray="4 2"
              isAnimationActive={false}
            />
          )}

          {/* Star rating lines (star axis) */}
          {activeViews.has('Avg Rating') && (
            <Line
              yAxisId="star"
              type="monotone"
              dataKey="avg_star_rating"
              name="Avg Rating"
              stroke={COLORS.starAvg}
              dot={{ r: 2, fill: COLORS.starMa7 }}
              strokeWidth={0}
              connectNulls={false}
              isAnimationActive={false}
            />
          )}
          {activeViews.has('Star 7-Day MA') && (
            <Line
              yAxisId="star"
              type="monotone"
              dataKey="star_ma_7"
              name="Star 7-Day MA"
              stroke={COLORS.starMa7}
              dot={false}
              strokeWidth={1.5}
              strokeDasharray="3 2"
              connectNulls
              isAnimationActive={false}
            />
          )}
          {activeViews.has('Star 14-Day MA') && (
            <Line
              yAxisId="star"
              type="monotone"
              dataKey="star_ma_14"
              name="Star 14-Day MA"
              stroke={COLORS.starMa14}
              dot={false}
              strokeWidth={2}
              connectNulls
              isAnimationActive={false}
            />
          )}
          {activeViews.has('Star 28-Day MA') && (
            <Line
              yAxisId="star"
              type="monotone"
              dataKey="star_ma_28"
              name="Star 28-Day MA"
              stroke={COLORS.starMa28}
              dot={false}
              strokeWidth={2.5}
              connectNulls
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Resizable>
      </div>

      {/* Footer attribution */}
      <div style={{ fontSize: '0.65rem', color: '#554a20', marginTop: 8, paddingTop: 6, borderTop: '1px solid #2a2000' }}>
        URI (Upstart Review Index) tracks daily new reviews as a real-time proxy for
        loan origination volume. Nearly all reviews are from customers who just received a loan.
      </div>
    </div>
  );
};

export default CompanyReviewsChart;
